import { useEffect, useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Globe, Eye, Facebook } from "lucide-react";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "st_visit_tracked_v1";

function formatCount(n) {
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("it-IT");
}

export default function Footer() {
  const [visits, setVisits] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const alreadyTracked = sessionStorage.getItem(SESSION_KEY) === "1";
        const url = alreadyTracked ? `${API}/visits` : `${API}/visits/track`;
        const method = alreadyTracked ? "get" : "post";
        const res = await axios({ url, method });
        if (!cancelled) {
          setVisits(res.data?.count ?? null);
          if (!alreadyTracked) sessionStorage.setItem(SESSION_KEY, "1");
        }
      } catch (e) {
        if (!cancelled) setVisits(null);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <footer data-testid="site-footer" className="relative bg-brand-ink text-white pt-20 pb-10 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.05] grain pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4">
              <img src={SITE.logoUrl} alt="Sicilia Traslochi" className="h-16 w-16 object-contain" />
              <div>
                <div className="font-display font-extrabold text-xl tracking-tight">SICILIA TRASLOCHI</div>
                <div className="text-xs tracking-[0.18em] uppercase text-brand-muted">
                  Movimentiamo il futuro con cura
                </div>
              </div>
            </div>
            <p className="mt-6 text-white/70 max-w-md leading-relaxed">
              Da oltre 18 anni il riferimento dei traslochi in Sicilia. Qualità senza compromessi,
              copertura assicurativa totale, attenzione maniacale al dettaglio.
            </p>

            {/* Social links */}
            <div className="mt-6 flex items-center gap-3">
              <a
                href={SITE.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Sicilia Traslochi su Facebook"
                data-testid="footer-facebook-link"
                className="group inline-flex items-center gap-2 px-4 h-11 rounded-full bg-[#1877F2] hover:bg-[#0e63d2] text-white font-semibold text-sm transition-colors"
              >
                <Facebook className="w-4 h-4 fill-current" />
                Seguici su Facebook
              </a>
            </div>

            {/* Visit counter */}
            <div
              data-testid="footer-visits-counter"
              className="mt-8 inline-flex items-center gap-3 px-4 py-2.5 rounded-full border border-white/10 bg-white/[0.04]"
            >
              <span className="relative flex items-center justify-center w-2 h-2">
                <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
                <span className="relative w-2 h-2 rounded-full bg-emerald-400" />
              </span>
              <Eye className="w-4 h-4 text-brand-muted" />
              <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-brand-muted">
                Visite al sito
              </span>
              <span
                data-testid="footer-visits-value"
                className="font-display font-extrabold text-white tabular-nums"
              >
                {visits === null ? "···" : formatCount(visits)}
              </span>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-brand-muted mb-5">Servizi</div>
            <ul className="space-y-3 text-white/80">
              <li><a href="#servizi" className="link-underline hover:text-white">Traslochi Casa</a></li>
              <li><a href="#servizi" className="link-underline hover:text-white">Traslochi Uffici</a></li>
              <li><a href="#servizi" className="link-underline hover:text-white">Noleggio Autoscale</a></li>
              <li><a href="#servizi" className="link-underline hover:text-white">Imballaggio Sicuro</a></li>
              <li><a href="#servizi" className="link-underline hover:text-white">Sgombero Magazzini e Case</a></li>
              <li><a href="#nord-italia" className="link-underline hover:text-white">Sicilia → Nord Italia</a></li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-brand-muted mb-5">Contatti</div>
            <ul className="space-y-3 text-white/80">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 shrink-0 text-brand-muted" />
                <span>{SITE.address}</span>
              </li>
              <li>
                <a href={`tel:${SITE.phoneTel}`} className="flex items-center gap-3 hover:text-white" data-testid="footer-phone-link">
                  <Phone className="w-4 h-4 text-brand-muted" />
                  <span className="font-semibold">{SITE.phone}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 hover:text-white">
                  <Mail className="w-4 h-4 text-brand-muted" />
                  <span>{SITE.email}</span>
                </a>
              </li>
              <li>
                <a href={`https://${SITE.site}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-white">
                  <Globe className="w-4 h-4 text-brand-muted" />
                  <span>{SITE.site}</span>
                </a>
              </li>
              <li>
                <a href={SITE.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-white" data-testid="footer-facebook-secondary-link">
                  <Facebook className="w-4 h-4 text-brand-muted" />
                  <span>Facebook</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/60 text-center sm:text-left">
          <div>© {new Date().getFullYear()} Sicilia Traslochi · Tutti i diritti riservati</div>
          <div className="tracking-wider uppercase">Caltanissetta · Sicilia · Italia</div>
          <div className="text-white/50">
            Sito realizzato da{" "}
            <a
              href="https://simomarketing.it"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="simomarketing-credit-link"
              className="font-display font-bold tracking-wide text-white hover:text-brand-muted transition-colors link-underline"
            >
              SIMOMARKETING
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
